<script setup lang="ts">
/**
 * CoreTextareaField
 *
 * Wrapper + `<textarea>` control with stable styling and minimal local rules.
 * See `CoreTextField` for the overall validation strategy (same approach).
 */
import { computed, inject, onBeforeUnmount, onMounted, ref, toRef, watch } from 'vue'
import CoreField from '../CoreField.vue'
import type { CoreErrorMessages, CoreFieldDensity, CoreFieldVariant, CoreRule } from '../types'
import { coreFormKey } from '../keys'
import { getCoreControlClasses } from '../controlClasses'
import { normalizeErrorMessages, useRulesValidation } from '../useRulesValidation'
import { useStableId } from '../useStableId'
import { resizeAutoGrowTextarea } from '@/lib/autoGrowTextarea'

const props = withDefaults(
  defineProps<{
    modelValue: string
    label?: string
    placeholder?: string
    description?: string
    errorMessages?: CoreErrorMessages
    rules?: CoreRule<string>[]
    variant?: CoreFieldVariant
    density?: CoreFieldDensity
    disabled?: boolean
    readonly?: boolean
    required?: boolean
    name?: string
    id?: string
    hideDetails?: 'auto' | 'always' | 'never'
    rows?: number
    autogrow?: boolean
    autogrowMaxHeight?: number | string
    inputClass?: string | string[] | Record<string, boolean>
    inputAttrs?: Record<string, unknown>
  }>(),
  {
    label: undefined,
    placeholder: undefined,
    description: undefined,
    errorMessages: undefined,
    rules: undefined,
    variant: 'outlined',
    density: 'default',
    disabled: false,
    readonly: false,
    required: false,
    name: undefined,
    id: undefined,
    hideDetails: 'auto',
    rows: 4,
    autogrow: false,
    autogrowMaxHeight: undefined,
    inputClass: undefined,
    inputAttrs: undefined,
  },
)

defineSlots<{
  after?: () => unknown
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const form = inject(coreFormKey, null)
const touched = ref(false)

const stableId = useStableId({
  id: toRef(props, 'id'),
  name: toRef(props, 'name'),
  prefix: 'core-field',
})

const textareaRef = ref<HTMLTextAreaElement | null>(null)
const autogrowOverflowY = ref<'hidden' | 'auto'>('hidden')
let resizeRaf: number | null = null

const valueRef = computed({
  get: () => props.modelValue,
  set: (next) => emit('update:modelValue', next),
})

const { ruleErrors, validateRules, resetRules } = useRulesValidation({
  value: valueRef,
  rules: toRef(props, 'rules'),
})

const externalErrors = computed(() => normalizeErrorMessages(props.errorMessages))
const hasExternalErrors = computed(() => externalErrors.value.length > 0)
const canShowErrors = computed(() => touched.value || form?.submitted.value)

const activeErrors = computed<string[]>(() => {
  if (!canShowErrors.value) return []
  if (hasExternalErrors.value) return externalErrors.value
  return ruleErrors.value
})

const invalid = computed(() => activeErrors.value.length > 0)
const controlClass = computed(() =>
  getCoreControlClasses({ variant: props.variant, density: props.density, invalid: invalid.value }),
)
const inputClasses = computed(() => [
  controlClass.value,
  props.autogrow ? 'resize-none' : 'resize-y',
  props.inputClass,
])

const resolvedAutogrowMaxHeight = computed(() => {
  if (props.autogrowMaxHeight === undefined || props.autogrowMaxHeight === null || props.autogrowMaxHeight === '') {
    return undefined
  }
  return typeof props.autogrowMaxHeight === 'number'
    ? `${props.autogrowMaxHeight}px`
    : props.autogrowMaxHeight
})

const textareaStyle = computed(() => {
  if (!props.autogrow) {
    return resolvedAutogrowMaxHeight.value
      ? { maxHeight: resolvedAutogrowMaxHeight.value }
      : undefined
  }

  return {
    ...(resolvedAutogrowMaxHeight.value ? { maxHeight: resolvedAutogrowMaxHeight.value } : {}),
    overflowY: autogrowOverflowY.value,
  }
})

const validateCurrent = async () => {
  touched.value = true
  if (props.disabled) {
    resetRules()
    return []
  }
  if (hasExternalErrors.value) return externalErrors.value
  return await validateRules()
}

const resetValidation = () => {
  touched.value = false
  resetRules()
}

let unregister: (() => void) | null = null
onMounted(() => {
  if (!form) return
  const key = props.name || stableId.value
  unregister = form.registerField({
    key,
    validate: validateCurrent,
    resetValidation,
  })
})

onBeforeUnmount(() => unregister?.())

const resizeTextarea = () => {
  if (!props.autogrow) return
  const el = textareaRef.value
  if (!el) return
  const parsedMaxHeight = Number.parseFloat(resolvedAutogrowMaxHeight.value ?? '')
  autogrowOverflowY.value = resizeAutoGrowTextarea(el, Number.isFinite(parsedMaxHeight) ? parsedMaxHeight : undefined)
    ? 'auto' : 'hidden'
}

const scheduleResize = () => {
  if (!props.autogrow) return
  if (resizeRaf !== null) cancelAnimationFrame(resizeRaf)
  resizeRaf = requestAnimationFrame(() => {
    resizeTextarea()
    resizeRaf = null
  })
}

watch(
  () => props.modelValue,
  () => {
    if (props.autogrow) scheduleResize()
  },
)

watch(
  () => props.autogrow,
  (next) => {
    if (!next && textareaRef.value) {
      textareaRef.value.style.height = ''
      textareaRef.value.style.removeProperty('field-sizing')
      autogrowOverflowY.value = 'hidden'
      return
    }
    scheduleResize()
  },
)

watch(resolvedAutogrowMaxHeight, scheduleResize)

onMounted(() => {
  if (props.autogrow) scheduleResize()
})

onBeforeUnmount(() => {
  if (resizeRaf !== null) cancelAnimationFrame(resizeRaf)
})

const onBlur = async () => {
  await validateCurrent()
}

const onInput = async (event: Event) => {
  const target = event.target as HTMLTextAreaElement
  valueRef.value = target.value
  scheduleResize()

  if (canShowErrors.value && !hasExternalErrors.value) {
    await validateRules()
  }
}
</script>

<template>
  <CoreField
    :id="stableId"
    :name="name"
    :label="label"
    :description="description"
    :error-messages="activeErrors"
    :required="required"
    :disabled="disabled"
    :readonly="readonly"
    :variant="variant"
    :density="density"
    :hide-details="hideDetails"
  >
    <template #default="{ id: controlId, describedBy, invalid: slotInvalid, disabled: slotDisabled, readonly: slotReadonly, required: slotRequired }">
      <textarea
        ref="textareaRef"
        v-bind="inputAttrs"
        :id="controlId"
        :name="name"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="slotDisabled"
        :readonly="slotReadonly"
        :required="slotRequired"
        :aria-invalid="slotInvalid ? 'true' : undefined"
        :aria-describedby="describedBy"
        :rows="rows"
        :style="textareaStyle"
        :class="inputClasses"
        @input="onInput"
        @blur="onBlur"
      />
      <div v-if="$slots.after" class="mt-1 text-xs text-on-surface-variant">
        <slot name="after" />
      </div>
    </template>
  </CoreField>
</template>
